import { cookies } from "next/headers"

export interface AuthSessionUser {
  id: number | string
  name: string
  email: string
  roles?: string[]
}

export interface AuthSession {
  wordpressSessionCookie: string
  user: AuthSessionUser | null
  expiresAt: string | null
}

export interface WordPressLoggedInIdentity {
  username: string
  expiry: Date
}

export interface SetAuthSessionInput {
  wordpressSessionCookie: string
  user?: AuthSessionUser | null
}

const DEFAULT_SESSION_MAX_AGE_SECONDS = 60 * 60 * 24 * 7 // 7 days
const COOKIE_PAIR_REGEX = /^[^=\s;]+=[^;]*$/

const decodeBase64Url = (value: string): string => {
  const normalizedValue = value.replace(/-/g, "+").replace(/_/g, "/")
  const paddingLength = (4 - (normalizedValue.length % 4)) % 4
  const paddedValue = `${normalizedValue}${"=".repeat(paddingLength)}`
  return Buffer.from(paddedValue, "base64").toString("utf8")
}

const parseUserCookie = (value: string | undefined): AuthSessionUser | null => {
  if (!value) {
    return null
  }

  try {
    return JSON.parse(value) as AuthSessionUser
  } catch {
    return null
  }
}

const tryDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const extractCookiePairs = (value: string): string[] => {
  return value
    .split(";")
    .map((part) => part.trim())
    .filter((part) => COOKIE_PAIR_REGEX.test(part))
}

export const normalizeSessionCookieToken = (
  value: string | null | undefined
): string | null => {
  const trimmedValue = value?.trim()

  if (!trimmedValue) {
    return null
  }

  const decodedValue = tryDecodeURIComponent(trimmedValue)
  const normalizedPairs = extractCookiePairs(decodedValue)

  if (normalizedPairs.length === 0) {
    return null
  }

  return normalizedPairs.join("; ")
}

const isProductionEnvironment = process.env.NODE_ENV === "production"

export const getJwtExpirationDate = (token: string): Date | null => {
  try {
    const tokenParts = token.split(".")

    if (tokenParts.length !== 3) {
      return null
    }

    const payloadText = decodeBase64Url(tokenParts[1] ?? "")
    const payload = JSON.parse(payloadText) as { exp?: number }

    if (!payload.exp || Number.isNaN(payload.exp)) {
      return null
    }

    return new Date(payload.exp * 1000)
  } catch {
    return null
  }
}

const getFallbackExpirationDate = (): Date => {
  const expiresAt = Date.now() + DEFAULT_SESSION_MAX_AGE_SECONDS * 1000
  return new Date(expiresAt)
}

/**
 * Parses the wordpress_logged_in_* cookie from a cookie string.
 * Format: username|expiry|token|hmac
 * Returns the username and expiry date.
 */
export const parseWordPressLoggedInCookie = (
  cookieString: string
): WordPressLoggedInIdentity | null => {
  try {
    const match = cookieString
      .split("; ")
      .find((part) => part.startsWith("wordpress_logged_in_"))

    if (!match) {
      return null
    }

    const cookieValue = match.split("=").slice(1).join("=")
    const decoded = decodeURIComponent(cookieValue)
    const parts = decoded.split("|")

    if (parts.length < 2) {
      return null
    }

    const username = parts[0]
    const expiryTimestamp = Number.parseInt(parts[1] ?? "0", 10)

    if (!username || Number.isNaN(expiryTimestamp)) {
      return null
    }

    return {
      username,
      expiry: new Date(expiryTimestamp * 1000),
    }
  } catch {
    return null
  }
}

/**
 * Determines the session expiration date.
 * Priority: WordPress cookie expiry > JWT expiry > 7-day fallback
 */
export const getSessionExpirationDate = (
  wordpressSessionCookie: string
): Date => {
  const wpIdentity = parseWordPressLoggedInCookie(wordpressSessionCookie)

  if (wpIdentity?.expiry && wpIdentity.expiry > new Date()) {
    return wpIdentity.expiry
  }

  const jwtExpiry = getJwtExpirationDate(wordpressSessionCookie)

  if (jwtExpiry && jwtExpiry > new Date()) {
    return jwtExpiry
  }

  return getFallbackExpirationDate()
}

export const setSession = async ({
  wordpressSessionCookie,
  user,
}: SetAuthSessionInput): Promise<void> => {
  const cookieStore = await cookies()
  const expires = getSessionExpirationDate(wordpressSessionCookie)

  cookieStore.set("wp_session_token", wordpressSessionCookie, {
    httpOnly: true,
    secure: isProductionEnvironment,
    sameSite: "lax" as const,
    path: "/",
    expires,
  })

  if (user) {
    cookieStore.set("wp_session_user", JSON.stringify(user), {
      httpOnly: true,
      secure: isProductionEnvironment,
      sameSite: "lax" as const,
      path: "/",
      expires,
    })

    return
  }

  cookieStore.delete("wp_session_user")
}

export const clearSession = async (): Promise<void> => {
  const cookieStore = await cookies()
  cookieStore.delete("wp_session_token")
  cookieStore.delete("wp_session_user")
}

export const getSession = async (): Promise<AuthSession | null> => {
  const cookieStore = await cookies()
  const wordpressSessionCookie = normalizeSessionCookieToken(
    cookieStore.get("wp_session_token")?.value
  )

  if (!wordpressSessionCookie) {
    return null
  }

  const userCookieValue = cookieStore.get("wp_session_user")?.value
  const user = parseUserCookie(userCookieValue)
  const expirationDate = getSessionExpirationDate(wordpressSessionCookie)

  return {
    wordpressSessionCookie,
    user,
    expiresAt: expirationDate?.toISOString() ?? null,
  }
}

export const getRequiredSessionCookie = async (): Promise<string | null> => {
  const session = await getSession()
  const token = session?.wordpressSessionCookie?.trim()

  return token || null
}
